// Shared browser-frame mockup that drops a banner image into the exact spot each
// ad placement occupies. Used on the Advertise booking page and the public
// placements explainer page so visitors can picture their own ad in each slot.

const MOCK_RATIO = { home_top: 1200 / 120, home: 1200 / 120, browse: 1200 / 120, browse_bottom: 1200 / 180, browse_sidebar: 600 / 500, listing: 600 / 500 };

const MockSkeletonCard = () => (
  <div className="rounded border border-slate-100 p-1">
    <div className="w-full aspect-square rounded bg-slate-100" />
    <div className="mt-1 h-1 w-3/4 rounded bg-slate-200" />
    <div className="mt-0.5 h-1 w-1/2 rounded bg-slate-100" />
  </div>
);

const MockAdSlot = ({ placement, image, onDims }) => {
  const target = MOCK_RATIO[placement] || 1;
  return (
    <div className="relative rounded border-2 border-dashed border-[#F5551E] bg-[#FFF4F0] overflow-hidden flex items-center justify-center" style={{ aspectRatio: `${target}` }} data-testid="mockup-ad-slot">
      {image
        ? <img src={image} onLoad={(e) => onDims && onDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })} alt="Your banner preview" className="w-full h-full object-cover" data-testid="mockup-banner-img" />
        : <span className="text-[8px] sm:text-[10px] font-bold text-[#F5551E] tracking-wide px-1 text-center">YOUR AD HERE</span>}
      {image && (
        <>
          <span className="pointer-events-none absolute inset-[7%] border border-dashed border-white/85 rounded-sm" data-testid="mockup-safe-area" />
          <span className="pointer-events-none absolute bottom-1 left-1 text-[7px] sm:text-[8px] font-bold text-white/90 bg-black/45 rounded px-1">Safe area</span>
        </>
      )}
    </div>
  );
};

export function BannerMockup({ placement, image, onDims }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="banner-mockup">
      <div className="flex items-center gap-1 px-2 py-1 bg-slate-100 border-b border-slate-200">
        <span className="w-2 h-2 rounded-full bg-red-300" />
        <span className="w-2 h-2 rounded-full bg-amber-300" />
        <span className="w-2 h-2 rounded-full bg-emerald-300" />
        <span className="ml-2 flex-1 h-2.5 rounded bg-white" />
      </div>
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-slate-100">
        <span className="w-3 h-3 rounded-full bg-[#F5551E]/80" />
        <span className="text-[9px] font-extrabold text-[#12234A]">critterposts.ca</span>
        <span className="ml-auto flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="w-5 h-1.5 rounded bg-slate-200" />)}</span>
        <span className="w-8 h-2.5 rounded bg-[#F5551E]/70" />
      </div>
      <div className="p-2">
        {placement === "home_top" && (
          <>
            <MockAdSlot placement="home_top" image={image} onDims={onDims} />
            <div className="grid grid-cols-4 gap-1.5 mt-2">{Array.from({ length: 8 }).map((_, i) => <MockSkeletonCard key={i} />)}</div>
          </>
        )}
        {placement === "home" && (
          <>
            <div className="grid grid-cols-4 gap-1.5">{Array.from({ length: 8 }).map((_, i) => <MockSkeletonCard key={i} />)}</div>
            <div className="mt-2"><MockAdSlot placement="home" image={image} onDims={onDims} /></div>
          </>
        )}
        {placement === "browse" && (
          <>
            <div className="flex gap-1 mb-2">
              <span className="flex-1 h-4 rounded bg-slate-100" />
              <span className="w-24 h-4 rounded bg-slate-100" />
              <span className="w-10 h-4 rounded bg-[#F5551E]/70" />
            </div>
            <MockAdSlot placement="browse" image={image} onDims={onDims} />
            <div className="flex gap-2 mt-2">
              <div className="w-1/5 space-y-1">{Array.from({ length: 5 }).map((_, i) => <span key={i} className="block h-2 rounded bg-slate-100" />)}</div>
              <div className="flex-1 grid grid-cols-3 gap-1.5">{Array.from({ length: 6 }).map((_, i) => <MockSkeletonCard key={i} />)}</div>
            </div>
          </>
        )}
        {placement === "browse_bottom" && (
          <>
            <div className="flex gap-1 mb-2">
              <span className="flex-1 h-4 rounded bg-slate-100" />
              <span className="w-24 h-4 rounded bg-slate-100" />
              <span className="w-10 h-4 rounded bg-[#F5551E]/70" />
            </div>
            <div className="flex gap-2">
              <div className="w-1/5 space-y-1">{Array.from({ length: 5 }).map((_, i) => <span key={i} className="block h-2 rounded bg-slate-100" />)}</div>
              <div className="flex-1 grid grid-cols-3 gap-1.5">{Array.from({ length: 6 }).map((_, i) => <MockSkeletonCard key={i} />)}</div>
            </div>
            <div className="mt-2"><MockAdSlot placement="browse_bottom" image={image} onDims={onDims} /></div>
          </>
        )}
        {placement === "browse_sidebar" && (
          <>
            <div className="flex gap-1 mb-2">
              <span className="flex-1 h-4 rounded bg-slate-100" />
              <span className="w-24 h-4 rounded bg-slate-100" />
              <span className="w-10 h-4 rounded bg-[#F5551E]/70" />
            </div>
            <div className="flex gap-2">
              <div className="w-1/3 space-y-1.5">
                {Array.from({ length: 5 }).map((_, i) => <span key={i} className="block h-2 rounded bg-slate-100" />)}
                <span className="block h-3 rounded bg-[#F5551E]/70" />
                <MockAdSlot placement="browse_sidebar" image={image} onDims={onDims} />
              </div>
              <div className="flex-1 grid grid-cols-3 gap-1.5">{Array.from({ length: 9 }).map((_, i) => <MockSkeletonCard key={i} />)}</div>
            </div>
          </>
        )}
        {placement === "listing" && (
          <div className="flex gap-2">
            <div className="w-1/2">
              <div className="w-full aspect-square rounded bg-slate-100" />
              <div className="mt-1 grid grid-cols-4 gap-1">{Array.from({ length: 4 }).map((_, i) => <span key={i} className="aspect-square rounded bg-slate-100" />)}</div>
            </div>
            <div className="flex-1 space-y-1.5">
              <div className="h-2.5 w-3/4 rounded bg-slate-200" />
              <div className="h-2 w-1/3 rounded bg-[#F5551E]/70" />
              <div className="h-1.5 w-full rounded bg-slate-100" />
              <div className="h-1.5 w-5/6 rounded bg-slate-100" />
              <div className="rounded border border-slate-100 p-1 mt-1">
                <div className="h-1.5 w-1/2 rounded bg-slate-200" />
                <div className="mt-1 h-3 w-full rounded bg-[#F5551E]/70" />
              </div>
              <MockAdSlot placement="listing" image={image} onDims={onDims} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
